import React, { useState, useCallback, useEffect } from 'react';

import omit from 'lodash/omit';

import Modal from 'components/uiLibrary/Modal';
import Typography from 'components/uiLibrary/Typography';

import DatesSelector from 'components/Globals/Layout/Header/GlobalSearch/DatesSelector';

import { DATE_FORMATS, TP } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import { FILTER_TYPES, ROUTE_DATE_FORMATS } from 'operabase-router/constants';

import classes from './TabHeader.module.scss';

const DatesSelectorModal = ({ isOpen, onClose, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const navigate = useNavigate();
  const { applyFilters } = usePageContext();
  const { linkProps, page } = obRouteContext || {};
  const { filters } = linkProps || {};
  const [dates, setDates] = useState({});

  useEffect(() => {
    const currentDateFilters = filters?.[FILTER_TYPES.DATE] || {};
    const hasDateFrom = currentDateFilters.date_from;
    const hasDateTo = currentDateFilters.date_to;

    // NOTE: Check if current filters match any predefined patterns
    const today = createDate();
    const isUpcomingFilter = hasDateFrom === today.format(ROUTE_DATE_FORMATS.STANDARD) && !hasDateTo;
    const isTodayFilter =
      hasDateFrom === today.format(ROUTE_DATE_FORMATS.STANDARD) &&
      hasDateTo === today.format(ROUTE_DATE_FORMATS.STANDARD);
    const isNext15DaysFilter =
      hasDateFrom === createDate(currentDateFilters.date_from).format(DATE_FORMATS.FULL_DATE) &&
      hasDateTo ===
        createDate()
          .add(15, 'day')
          .format(DATE_FORMATS.FULL_DATE);

    const isNext30DaysFilter =
      hasDateFrom === createDate(currentDateFilters.date_from).format(DATE_FORMATS.FULL_DATE) &&
      hasDateTo ===
        createDate()
          .add(30, 'day')
          .format(DATE_FORMATS.FULL_DATE);

    // NOTE: Check if it's a year filter (Jan 1 to Dec 31 of the same year)
    const isYearFilter =
      hasDateFrom &&
      hasDateTo &&
      hasDateFrom.match(/^\d{4}-01-01$/) &&
      hasDateTo.match(/^\d{4}-12-31$/) &&
      hasDateFrom.substring(0, 4) === hasDateTo.substring(0, 4);

    // NOTE: If we have custom date filters that don't match predefined patterns, preserve them in selectedDates
    if (
      (hasDateFrom || hasDateTo) &&
      !(isUpcomingFilter || isTodayFilter || isNext15DaysFilter || isNext30DaysFilter || isYearFilter)
    ) {
      setDates({
        fromDate: hasDateFrom || null,
        toDate: hasDateTo || null,
      });
    }
  }, [filters]);

  const handleUpdateCategory = useCallback(updatedDates => {
    setDates(updatedDates);
  }, []);

  const handleDatesSelectorApply = useCallback(
    selectedDates => {
      if (selectedDates.fromDate || selectedDates.toDate) {
        const dateFilters = {
          ...filters,
          [FILTER_TYPES.WATCH_ONLINE]: null,
          [FILTER_TYPES.TICKET]: null,
          [FILTER_TYPES.DATE]: {
            ...(selectedDates.fromDate && { date_from: selectedDates.fromDate }),
            ...(selectedDates.toDate && { date_to: selectedDates.toDate }),
          },
        };

        const { entity: contextEntity, entityType: contextEntityType } = page || {};

        applyFilters({
          entityType: contextEntityType,
          entity: contextEntity,
          filters: dateFilters,
        });
      } else {
        navigate.to(
          navigate.getLinkProps({
            ...linkProps,
            filters: {
              ...omit(filters, [FILTER_TYPES.DATE]),
            },
          }),
        );
      }

      onClose();
    },
    [applyFilters, filters, linkProps, navigate, onClose, page],
  );

  const handleApply = useCallback(() => {
    handleDatesSelectorApply(dates);
    onClose();
  }, [dates, handleDatesSelectorApply, onClose]);

  const handleClear = useCallback(() => {
    setDates({});
    handleDatesSelectorApply({});
  }, [handleDatesSelectorApply]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      styles={{
        modalContainer: classes.datesModalContainer,
        modalContent: classes.datesModalContent,
      }}
      title={
        <Typography variant="h2" size={16} weight="bold">
          {t(`${TP}.m_DATES`)}
        </Typography>
      }
      allowMobileDrawer
    >
      <DatesSelector
        handleUpdateCategory={handleUpdateCategory}
        categoryDetails={dates}
        onApply={handleApply}
        onClear={handleClear}
        trackingData={trackingData}
      />
    </Modal>
  );
};

export default DatesSelectorModal;
